import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  useMutation,
  UseQueryResult,
  UseInfiniteQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { IAlarm } from '_/api/alarms/alarms.api';
import {
  fetchDelivery,
  fetchDeliveryStats,
  fetchDeliveryComments,
  fetchDeliveryTransitions,
  createDeliveryComment,
  fetchDeliveryTopLevelRollup,
  fetchDeliveryFilteredSnapshots,
  fetchDeliveryMetrics,
  fetchDeliveryAlarms,
  updateDeliveryAlarm,
  triggerDeliveryAlarm,
  resolveDeliveryAlarm,
  fetchDeliveryNotifications,
  fetchDeliverySources,
  fetchDeliveryDestinationPushes,
  updateDelivery,
  fetchOrganizationDeliveries,
  cancelDelivery,
} from '_/api/deliveries/deliveries.api';
import { IComment } from '_/common/types/comments.types';
import { IDelivery, IDeliveryStatsType } from '_/types/deliveries/delivery.types';
import { notification } from 'antd';

export const DELIVERY_QUERY_KEY = (deliveryId: string): string[] => ['delivery', deliveryId];
export const DELIVERY_STATS_QUERY_KEY = (deliveryId: string): string[] => ['delivery', deliveryId, 'stats'];
export const DELIVERY_COMMENTS_QUERY_KEY = (deliveryId: string): string[] => ['delivery', deliveryId, 'comments'];
export const DELIVERY_TRANSITIONS_QUERY_KEY = (deliveryId: string): string[] => ['delivery', deliveryId, 'transitions'];
export const DELIVERY_METRICS_QUERY_KEY = (deliveryId: string): string[] => ['delivery', deliveryId, 'metrics'];
export const DELIVERY_ALARMS_QUERY_KEY = (deliveryId: string): string[] => ['delivery', deliveryId, 'alarms'];
export const DELIVERY_NOTIFICATIONS_QUERY_KEY = (deliveryId: string): string[] => ['delivery', deliveryId, 'notifications'];
export const DELIVERY_SOURCES_QUERY_KEY = (deliveryId: string): string[] => ['delivery', deliveryId, 'sources'];
export const DELIVERY_DESTINATION_PUSHES_QUERY_KEY = (
  deliveryId: string,
  filter: string,
  limit?: number,
): (string | number | undefined)[] => ['delivery', deliveryId, 'destination-pushes', filter, limit];
export const DELIVERY_SNAPSHOTS_QUERY_KEY = (deliveryId: string, limit?: number | null): string[] => [
  'delivery',
  deliveryId,
  'snapshots',
  'rollup',
  ...(limit !== undefined ? [String(limit)] : []),
];
export const DELIVERY_FILTERED_SNAPSHOTS_QUERY_KEY = (deliveryId: string, filter: string): string[] => [
  'delivery',
  deliveryId,
  'snapshots',
  'filtered',
  filter,
];

/**
 * Hook to fetch a single delivery by ID
 */
export const useDelivery = (deliveryId?: string): UseQueryResult<IDelivery> => {
  return useQuery({
    queryKey: DELIVERY_QUERY_KEY(deliveryId || ''),
    queryFn: () => fetchDelivery(deliveryId!),
    enabled: !!deliveryId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: (query) => {
      const delivery = query.state.data;
      // Auto-refresh every 30 seconds if delivery is in active state
      const activeStatuses = ['OPEN', 'PENDING', 'PENDING_QUEUE'];
      return delivery && activeStatuses.includes(delivery.status) ? 30000 : false;
    },
  });
};

/**
 * Hook to fetch delivery stats
 */
export const useDeliveryStats = (deliveryId?: string, deliveryStatus?: string): UseQueryResult<IDeliveryStatsType | null> => {
  return useQuery({
    queryKey: DELIVERY_STATS_QUERY_KEY(deliveryId || ''),
    queryFn: () => fetchDeliveryStats(deliveryId!),
    enabled: !!deliveryId,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: () => {
      // Auto-refresh every 20 seconds if delivery is in active state
      const activeStatuses = [
        'OPEN',
        'PENDING',
        'PENDING_QUEUE',
        'START_PENDING',
        'STARTING',
        'RUNNING',
        'FINISHING',
        'FAILING',
        'STOPPING',
        'STOP_PENDING',
      ];
      return deliveryStatus && activeStatuses.includes(deliveryStatus) ? 20_000 : 90_000;
    },
  });
};

/**
 * Hook to fetch delivery comments
 */
export const useDeliveryComments = (deliveryId?: string): UseQueryResult<IComment[]> => {
  return useQuery({
    queryKey: DELIVERY_COMMENTS_QUERY_KEY(deliveryId || ''),
    queryFn: () => fetchDeliveryComments(deliveryId!),
    enabled: !!deliveryId,
    staleTime: 30_000, // 30 seconds
  });
};

/**
 * Hook to fetch delivery transitions
 */
export const useDeliveryTransitions = (deliveryId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: DELIVERY_TRANSITIONS_QUERY_KEY(deliveryId || ''),
    queryFn: () => fetchDeliveryTransitions(deliveryId!),
    enabled: !!deliveryId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch delivery metrics (for dashboard charts)
 */
export const useDeliveryMetrics = (deliveryId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: DELIVERY_METRICS_QUERY_KEY(deliveryId || ''),
    queryFn: () => fetchDeliveryMetrics(deliveryId!),
    enabled: !!deliveryId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch delivery alarms
 */
export const useDeliveryAlarms = (deliveryId?: string): UseQueryResult<IAlarm[]> => {
  return useQuery({
    queryKey: DELIVERY_ALARMS_QUERY_KEY(deliveryId || ''),
    queryFn: () => fetchDeliveryAlarms(deliveryId!),
    enabled: !!deliveryId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to update a delivery
 */
export const useUpdateDelivery = (deliveryId?: string): UseMutationResult<any, Error, any> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (delivery: any) => updateDelivery(deliveryId!, delivery),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DELIVERY_QUERY_KEY(deliveryId!) });
      notification.success({
        message: 'Delivery saved',
      });
    },
    onError: (error: any) => {
      console.error('Error saving delivery', error);
      notification.error({
        message: error.message || 'Error saving delivery',
      });
    },
  });
};

/**
 * Hook to update a delivery alarm
 */
export const useUpdateDeliveryAlarm = (
  deliveryId?: string,
): UseMutationResult<any, Error, { deliveryAlarmId: string; deliveryAlarm: any }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deliveryAlarmId, deliveryAlarm }: { deliveryAlarmId: string; deliveryAlarm: any }) =>
      updateDeliveryAlarm(deliveryAlarmId, deliveryAlarm),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DELIVERY_ALARMS_QUERY_KEY(deliveryId!) });
      notification.success({
        message: 'Delivery Alarm Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error updating delivery alarm', error);
      notification.error({
        message: error.message || 'Error updating delivery alarm',
      });
    },
  });
};

/**
 * Hook to trigger a delivery alarm
 */
export const useTriggerDeliveryAlarm = (deliveryId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deliveryAlarmId: string) => triggerDeliveryAlarm(deliveryAlarmId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DELIVERY_ALARMS_QUERY_KEY(deliveryId!) });
      notification.success({
        message: 'Alarm Triggered',
      });
    },
    onError: (error: any) => {
      console.error('Failed to trigger alarm', error);
      notification.error({
        message: error.message || 'Failed to trigger alarm',
      });
    },
  });
};

/**
 * Hook to resolve a delivery alarm
 */
export const useResolveDeliveryAlarm = (deliveryId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deliveryAlarmId: string) => resolveDeliveryAlarm(deliveryAlarmId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DELIVERY_ALARMS_QUERY_KEY(deliveryId!) });
      notification.success({
        message: 'Alarm Resolved',
      });
    },
    onError: (error: any) => {
      console.error('Failed to resolve alarm', error);
      notification.error({
        message: error.message || 'Failed to resolve alarm',
      });
    },
  });
};

/**
 * Hook to fetch delivery notifications
 */
export const useDeliveryNotifications = (deliveryId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: DELIVERY_NOTIFICATIONS_QUERY_KEY(deliveryId || ''),
    queryFn: () => fetchDeliveryNotifications(deliveryId!),
    enabled: !!deliveryId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch delivery sources
 */
export const useDeliverySources = (deliveryId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: DELIVERY_SOURCES_QUERY_KEY(deliveryId || ''),
    queryFn: () => fetchDeliverySources(deliveryId!),
    enabled: !!deliveryId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch delivery destination pushes
 */
export const useDeliveryDestinationPushes = (deliveryId?: string, filter = '', limit = 50): UseQueryResult<any> => {
  return useQuery({
    queryKey: DELIVERY_DESTINATION_PUSHES_QUERY_KEY(deliveryId || '', filter, limit),
    queryFn: () => fetchDeliveryDestinationPushes(deliveryId!, filter, limit),
    enabled: !!deliveryId && !!filter,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to create a delivery comment
 */
export const useCreateDeliveryComment = (deliveryId?: string): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (comment: string) => createDeliveryComment(deliveryId!, comment),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DELIVERY_COMMENTS_QUERY_KEY(deliveryId!) });
      notification.success({
        message: 'Comment Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error saving comment', error);
      notification.error({
        message: error.message || 'Error saving comment',
      });
    },
  });
};

/**
 * Hook to fetch delivery top-level rollup (root snapshots with stats)
 */
export const useDeliveryTopLevelRollup = (deliveryId?: string, limit?: number | null): UseInfiniteQueryResult<any> => {
  return useInfiniteQuery({
    queryKey: DELIVERY_SNAPSHOTS_QUERY_KEY(deliveryId || '', limit),
    queryFn: ({ pageParam }) => fetchDeliveryTopLevelRollup(deliveryId!, limit, pageParam),
    getNextPageParam: (lastPage) => lastPage.cursor,
    initialPageParam: undefined as string | undefined,
    enabled: !!deliveryId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch filtered delivery snapshots
 */
export const useDeliveryFilteredSnapshots = (
  deliveryId?: string,
  filter = 'status||in||PENDING_QA,QA,PASSED_QA,IN_PROGRESS,FAILED,FIXING,ESCALATED,FAILED_QA',
  enabled = true,
): UseInfiniteQueryResult<any> => {
  return useInfiniteQuery({
    queryKey: DELIVERY_FILTERED_SNAPSHOTS_QUERY_KEY(deliveryId || '', filter),
    queryFn: ({ pageParam }) => fetchDeliveryFilteredSnapshots(deliveryId!, filter, 100, pageParam),
    getNextPageParam: (lastPage) => lastPage.cursor,
    initialPageParam: undefined as string | undefined,
    enabled: !!deliveryId && enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch organization deliveries
 */
export const useOrganizationDeliveries = (orgId?: string, filter?: string): UseInfiniteQueryResult<any> => {
  return useInfiniteQuery({
    queryKey: ['organization', orgId, 'deliveries', filter || 'all'],
    queryFn: ({ pageParam }) => fetchOrganizationDeliveries(orgId!, filter, pageParam),
    getNextPageParam: (lastPage) => lastPage.cursor,
    initialPageParam: undefined as string | undefined,
    enabled: !!orgId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to cancel a delivery
 */
export const useCancelDelivery = (): UseMutationResult<any, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deliveryId: string) => cancelDelivery(deliveryId),
    onSuccess: (_, deliveryId) => {
      // Invalidate delivery queries
      void queryClient.invalidateQueries({ queryKey: DELIVERY_QUERY_KEY(deliveryId) });
      void queryClient.invalidateQueries({ queryKey: ['organization'] }); // Invalidate org deliveries lists
      void queryClient.invalidateQueries({ queryKey: ['flow'] }); // Invalidate flow deliveries lists

      notification.success({
        message: 'Delivery Cancelled',
      });
    },
    onError: (error: any) => {
      console.error('Error cancelling delivery', error);
      notification.error({
        message: error.message || 'Error cancelling delivery',
      });
    },
  });
};
